import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Activity, BarChart3, Cpu, LogOut, MoreHorizontal, Settings, Shield, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth-store";
import { MENU } from "./labels";
import { MENU_ITEMS, isCurrent, type MenuItem } from "./menu";

function item(href: string): MenuItem {
  const found = MENU_ITEMS.find((i) => i.href === href);
  if (!found) throw new Error(`No menu item for ${href}`);
  return found;
}

/** The four tabs before "More" (docs/new-look/PLAN.md, step 1.2). */
export const PHONE_TABS: MenuItem[] = [
  { href: "/dashboard", label: MENU.home, icon: BarChart3 },
  item("/ideas"),
  item("/blueprints"),
  { ...item("/devices"), icon: Cpu },
];

/** Everything else, in the "More" sheet. */
export function moreItems(isGlobalAdmin: boolean): MenuItem[] {
  return [
    item("/team"),
    item("/connections"),
    item("/teams"),
    item("/projects"),
    { href: "/activity", label: "Activity", icon: Activity },
    ...(isGlobalAdmin
      ? [
          { href: "/admin/users", label: "Admin", icon: Shield },
          { href: "/admin/activity", label: "Admin activity", icon: Activity },
        ]
      : []),
    { href: "/settings", label: MENU.settings, icon: Settings },
  ];
}

function TabButton({
  label,
  icon: Icon,
  current,
  onClick,
  expanded,
}: {
  label: string;
  icon: LucideIcon;
  current: boolean;
  onClick: () => void;
  expanded?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={current && expanded === undefined ? "page" : undefined}
      aria-expanded={expanded}
      className={cn(
        "flex h-[52px] min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-xl transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        current ? "bg-accent text-accent-foreground" : "text-muted-foreground active:bg-muted"
      )}
    >
      <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
      <span className="text-[11px] font-medium leading-none">{label}</span>
    </button>
  );
}

/** The phone tab bar: Home · Ideas · Blueprints · Devices · More. */
export function PhoneTabs() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [moreOpen, setMoreOpen] = useState(false);
  const more = moreItems(!!user?.isGlobalAdmin);
  const inMore = more.some((i) => isCurrent(i, pathname));

  useEffect(() => {
    setMoreOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!moreOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMoreOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [moreOpen]);

  const go = (href: string) => {
    setMoreOpen(false);
    void navigate(href);
  };

  return (
    <>
      {moreOpen && (
        <button
          type="button"
          aria-label="Close menu"
          onClick={() => {
            setMoreOpen(false);
          }}
          className="fixed inset-0 z-40 cursor-default bg-black/20"
        />
      )}
      <div className="fixed bottom-3 left-3 right-3 z-50 pb-safe">
        {moreOpen && (
          <div
            id="phone-more"
            role="region"
            aria-label="More"
            className="mb-2 overflow-hidden rounded-2xl border border-border bg-popover p-1.5 text-popover-foreground shadow-2xl"
          >
            <div className="mb-1 border-b border-border px-3 py-2.5">
              <p className="truncate text-sm font-medium text-foreground">{user?.name}</p>
              <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
            </div>
            {more.map((i) => {
              const current = isCurrent(i, pathname);
              return (
                <button
                  key={i.href}
                  type="button"
                  onClick={() => {
                    go(i.href);
                  }}
                  aria-current={current ? "page" : undefined}
                  className={cn(
                    "flex min-h-12 w-full items-center gap-3 rounded-xl px-3 text-left text-[15px] transition-colors",
                    current ? "bg-accent font-semibold text-accent-foreground" : "active:bg-muted"
                  )}
                >
                  <i.icon className="h-5 w-5" aria-hidden="true" />
                  {i.label}
                </button>
              );
            })}
            <div className="mt-1 border-t border-border pt-1">
              <button
                type="button"
                onClick={() => {
                  setMoreOpen(false);
                  logout();
                  void navigate("/login");
                }}
                className="flex min-h-12 w-full items-center gap-3 rounded-xl px-3 text-left text-[15px] text-destructive active:bg-muted"
              >
                <LogOut className="h-5 w-5" aria-hidden="true" />
                Sign out
              </button>
            </div>
          </div>
        )}
        <nav
          aria-label="Main"
          className="flex gap-0.5 rounded-2xl border border-border bg-background/95 p-1.5 shadow-[0_10px_30px_rgba(15,23,42,0.14)] backdrop-blur-xl"
        >
          {PHONE_TABS.map((t) => (
            <TabButton
              key={t.href}
              label={t.label}
              icon={t.icon}
              current={!moreOpen && isCurrent(t, pathname)}
              onClick={() => {
                go(t.href);
              }}
            />
          ))}
          <TabButton
            label={MENU.more}
            icon={MoreHorizontal}
            current={moreOpen || inMore}
            expanded={moreOpen}
            onClick={() => {
              setMoreOpen(!moreOpen);
            }}
          />
        </nav>
      </div>
    </>
  );
}
